import { useEffect, useState } from "react";
import type {
  MerchantSpec,
  MerchantEntities,
  TransactionDescriptor,
  InvoiceDescriptor,
  PayoutDescriptor,
  StaffDescriptor,
} from "@cpos/shared/src/dev-merchant/spec";
import type { CreateMerchantBloc } from "@/application/tools/create_merchant_bloc";
import { SectionCard, FormField } from "./shared";
import { inputClass } from "./styles";

interface Props {
  draft: MerchantSpec;
  bloc: CreateMerchantBloc;
  disabled?: boolean;
}

function buildTransactions(
  count: number,
  spreadDays: number,
  currency: "cad" | "usd",
): TransactionDescriptor[] {
  return Array.from({ length: count }, (_, i) => ({
    targetStatus: "Succeeded" as const,
    amount: (i + 1) * 1500 + 500,
    currency,
    createdDaysAgo:
      count > 1 ? Math.round((spreadDays * i) / (count - 1)) : spreadDays,
    entryMode: (i % 2 === 0 ? "contactless" : "chip") as "contactless" | "chip",
    cardLast4: "4242",
  }));
}

function buildInvoices(
  count: number,
  currency: "cad" | "usd",
): InvoiceDescriptor[] {
  return Array.from({ length: count }, (_, i) => ({
    amount: (i + 1) * 2500,
    currency,
    status: "Sent" as const,
    description: `Invoice ${i + 1}`,
    recipientName: `Customer ${i + 1}`,
    recipientContact: `customer${i + 1}@example.com`,
    deliveryMethod: "email" as const,
    createdDaysAgo: i * 3,
    dueInDays: 14,
  }));
}

function buildPayouts(
  count: number,
  currency: "cad" | "usd",
): PayoutDescriptor[] {
  return Array.from({ length: count }, (_, i) => ({
    amount: (i + 1) * 10000,
    currency,
    status: "paid" as const,
    createdDaysAgo: (i + 1) * 7,
    arrivalInDays: 2,
  }));
}

function buildStaff(count: number): StaffDescriptor[] {
  return Array.from({ length: count }, (_, i) => ({
    name: i === 0 ? "Shop Owner" : `Staff ${i}`,
    email: `staff${i}@cpos-seed.test`,
    isOwner: i === 0,
    isActive: true,
    pin: String(1000 + i).padStart(4, "0"),
  }));
}

export function EntitiesSection({ draft, bloc, disabled }: Props) {
  const { entities } = draft;
  const currency = draft.business.country === "US" ? "usd" : "cad";
  const [jsonText, setJsonText] = useState(() =>
    JSON.stringify(entities, null, 2),
  );
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  function patch(next: Partial<MerchantEntities>) {
    const updated = { ...entities, ...next };
    bloc.setDraft({ entities: updated });
    setJsonText(JSON.stringify(updated, null, 2));
    setJsonError(null);
  }

  // Keep the JSON textarea in sync with drafts loaded from outside this
  // section (preset hydration, dice rolls) while the panel is collapsed —
  // avoids clobbering in-progress edits while it's open.
  useEffect(() => {
    if (!advancedOpen) setJsonText(JSON.stringify(entities, null, 2));
  }, [entities, advancedOpen]);

  function handleAdvancedToggle(open: boolean) {
    setAdvancedOpen(open);
    if (open) setJsonText(JSON.stringify(entities, null, 2));
  }

  function handleJsonBlur() {
    try {
      const parsed = JSON.parse(jsonText) as MerchantEntities;
      bloc.setDraft({ entities: parsed });
      setJsonError(null);
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : "Invalid JSON");
    }
  }

  return (
    <SectionCard title="Entities">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField
          label="Succeeded transactions"
          hint="Spread evenly over the days window below."
        >
          <input
            type="number"
            min={0}
            className={inputClass}
            value={entities.transactions.length}
            onChange={(e) =>
              patch({
                transactions: buildTransactions(
                  Number(e.target.value) || 0,
                  entities.transactions[0]?.createdDaysAgo ?? 30,
                  currency,
                ),
              })
            }
            disabled={disabled}
          />
        </FormField>

        <FormField label="Spread over (days ago)">
          <input
            type="number"
            min={0}
            className={inputClass}
            value={
              entities.transactions.length
                ? Math.max(
                    ...entities.transactions.map((t) => t.createdDaysAgo ?? 0),
                  )
                : 30
            }
            onChange={(e) =>
              patch({
                transactions: buildTransactions(
                  entities.transactions.length,
                  Number(e.target.value) || 0,
                  currency,
                ),
              })
            }
            disabled={disabled}
          />
        </FormField>

        <FormField label="Invoices">
          <input
            type="number"
            min={0}
            className={inputClass}
            value={entities.invoices.length}
            onChange={(e) =>
              patch({
                invoices: buildInvoices(Number(e.target.value) || 0, currency),
              })
            }
            disabled={disabled}
          />
        </FormField>

        <FormField label="Payouts">
          <input
            type="number"
            min={0}
            className={inputClass}
            value={entities.payouts.length}
            onChange={(e) =>
              patch({
                payouts: buildPayouts(Number(e.target.value) || 0, currency),
              })
            }
            disabled={disabled}
          />
        </FormField>

        <FormField label="Staff">
          <input
            type="number"
            min={0}
            className={inputClass}
            value={entities.staff.length}
            onChange={(e) =>
              patch({ staff: buildStaff(Number(e.target.value) || 0) })
            }
            disabled={disabled}
          />
        </FormField>

        <FormField
          label="Receipts for first N transactions"
          hint="Email/SMS receipts created for the earliest N created transactions."
        >
          <input
            type="number"
            min={0}
            max={20}
            className={inputClass}
            value={entities.receiptsForFirstTransactions}
            onChange={(e) =>
              patch({
                receiptsForFirstTransactions: Number(e.target.value) || 0,
              })
            }
            disabled={disabled}
          />
        </FormField>
      </div>

      <details
        className="border-border mt-4 rounded-md border p-3"
        open={advancedOpen}
        onToggle={(e) =>
          handleAdvancedToggle((e.currentTarget as HTMLDetailsElement).open)
        }
      >
        <summary className="text-foreground cursor-pointer text-sm font-medium">
          Advanced (JSON)
        </summary>
        <div className="mt-3">
          <textarea
            className={`${inputClass} h-64 font-mono text-xs`}
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            onBlur={handleJsonBlur}
            disabled={disabled}
            spellCheck={false}
          />
          {jsonError && (
            <p className="mt-1 text-xs text-red-600">{jsonError}</p>
          )}
        </div>
      </details>
    </SectionCard>
  );
}
